import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/tokenService";
import "./SalarySlip.css";
import html2pdf from "html2pdf.js";
import { Capacitor } from "@capacitor/core";
import { savePdfBlob } from "../../../utils/nativePdfSave";

const formatCurrency = (value) => {
  const number = Number(value);
  if (Number.isFinite(number)) {
    return new Intl.NumberFormat("en-IN").format(number);
  }
  return "0";
};

const SalarySlip = ({ employeeId, externalMonth, externalYear }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [forcedPresentDays, setForcedPresentDays] = useState(null);
  const isClientView = Boolean(employeeId);

  useEffect(() => {
    if (isClientView) return;
    const loadUser = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/user-dashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUser(response.data?.currentUser || null);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Unable to fetch salary slip data. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [isClientView]);

  const currentMoment = moment().tz("Asia/Kolkata");
  const [selectedMonth, setSelectedMonth] = useState(currentMoment.month());
  const [selectedYear, setSelectedYear] = useState(currentMoment.year());
  useEffect(() => {
    if (!isClientView) return;
    if (typeof externalMonth === "number") {
      setSelectedMonth(externalMonth);
    }
    if (typeof externalYear === "number") {
      setSelectedYear(externalYear);
    }
    setForcedPresentDays(null);
  }, [externalMonth, externalYear, isClientView]);

  const slipRef = useRef(null);

  const years = useMemo(() => {
    const startYear = currentMoment.year() - 2;
    return Array.from({ length: 5 }, (_, idx) => startYear + idx);
  }, [currentMoment]);

  const months = moment.months();

  const slipMoment = moment()
    .tz("Asia/Kolkata")
    .year(selectedYear)
    .month(selectedMonth);
  const slipMonth = slipMoment.format("MMMM YYYY");
  const monthDays = slipMoment.daysInMonth();
  useEffect(() => {
    if (!isClientView || !employeeId) return;

    const loadEmployeeData = async () => {
      setLoading(true);
      setAttendanceRecords([]);
      setUser(null);
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/client/pay-slip/${employeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              month: selectedMonth + 1,
              year: selectedYear,
            },
          }
        );
        setUser(data.employee || null);
        setAttendanceRecords(data.attendance || []);
        if (Number.isFinite(data.presentDays) && data.presentDays > 0) {
          setForcedPresentDays(data.presentDays);
        } else {
          setForcedPresentDays(null);
        }
      } catch (error) {
        console.error("Unable to load employee payslip", error);
        toast.error("Unable to load employee salary and attendance.");
      } finally {
        setLoading(false);
      }
    };

    loadEmployeeData();
  }, [employeeId, isClientView, selectedMonth, selectedYear]);
  const presentDays = useMemo(() => {
    // If API provided a positive override, use it; otherwise derive from records
    if (forcedPresentDays != null && forcedPresentDays > 0) return forcedPresentDays;
    return attendanceRecords.reduce((count, record) => {
      // Prefer explicit date, but fall back to check-in/out timestamps.
      const recordDate = record?.date || record?.checkInTime || record?.checkOutTime;
      if (!recordDate) return count;

      const recordMoment = moment(recordDate).tz("Asia/Kolkata");
      const isSameMonth =
        recordMoment.month() === selectedMonth &&
        recordMoment.year() === selectedYear;

      // Mark present when status says Present OR when both check-in/out exist.
      const isMarkedPresent =
        record.status === "Present" ||
        (record.checkInTime && record.checkOutTime);

      return count + (isSameMonth && isMarkedPresent ? 1 : 0);
    }, 0);
  }, [attendanceRecords, selectedMonth, selectedYear, forcedPresentDays]);
  useEffect(() => {
    if (isClientView || !user) {
      setAttendanceRecords([]);
      setForcedPresentDays(null);
      return;
    }

    const loadAttendance = async () => {
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/attendance/user-data`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              page: 1,
              limit: 400, // fetch enough records to cover full month + history
            },
          }
        );
        const attendance = data?.userData?.attendance || [];
        setAttendanceRecords(attendance);
      } catch (error) {
        console.error("Unable to load attendance log", error);
      }
    };

    loadAttendance();
  }, [isClientView, user, selectedMonth, selectedYear]);
  const payroll = useMemo(() => {
    if (!user) return null;

    const toNumber = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const basic = toNumber(user.basic);
    const da = toNumber(user.da);
    const hra = toNumber(user.hra);
    const otherAllowance = toNumber(user.other_allowance);
    const lww = toNumber(user.leave_with_wages);
    const incentive = toNumber(user.incentive);
    const overtime = toNumber(user.overtime);
    const nightAllowance = toNumber(user.night_allowance);

    const gross =
      basic + da + hra + otherAllowance + lww + incentive + overtime + nightAllowance;

    const earnedRatio = monthDays > 0 ? presentDays / monthDays : 0;
    const computeEarned = (value) =>
      +(value * earnedRatio || 0).toFixed(2);

    const earnedBasic = computeEarned(basic);
    const earnedDa = computeEarned(da);
    const earnedHra = computeEarned(hra);
    const earnedOtherAllowance = computeEarned(otherAllowance);
    const earnedLww = computeEarned(lww);
    const earnedIncentive = computeEarned(incentive);
    const earnedOvertime = computeEarned(overtime);
    const earnedNightAllowance = computeEarned(nightAllowance);

    const earnedGross =
      earnedBasic +
      earnedDa +
      earnedHra +
      earnedOtherAllowance +
      earnedLww +
      earnedIncentive +
      earnedOvertime +
      earnedNightAllowance;

    const epfBase = earnedBasic + earnedDa;
    const epfComputed = +(epfBase * 0.12).toFixed(2);
    const epf = Math.min(epfComputed, 1800); // statutory upper cap
    const esic = +(earnedGross * 0.0075).toFixed(2);
    const lwfDeduction = toNumber(user.lwf);
    const professionalTax = toNumber(user.professional_tax);
    const advance = toNumber(user.advance);

    const totalDeduction =
      epf + esic + lwfDeduction + professionalTax + advance;
    const netTakeHome = +(earnedGross - totalDeduction).toFixed(2);

    return {
      basic,
      da,
      hra,
      otherAllowance,
      lww,
      incentive,
      overtime,
      nightAllowance,
      gross,
      earnedBasic,
      earnedDa,
      earnedHra,
      earnedOtherAllowance,
      earnedLww,
      earnedIncentive,
      earnedOvertime,
      earnedNightAllowance,
      earnedGross,
      epf,
      esic,
      lwfDeduction,
      professionalTax,
      advance,
      totalDeduction,
      netTakeHome,
    };
  }, [user, presentDays, monthDays]);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!slipRef.current || isDownloading) return;
    setIsDownloading(true);
    let exportHost = null;
    try {
      const exportNode = slipRef.current.cloneNode(true);
      exportHost = document.createElement("div");
      exportHost.style.cssText =
        "position:fixed;left:-9999px;top:0;width:210mm;background:#fff;z-index:-1;";
      exportHost.appendChild(exportNode);
      document.body.appendChild(exportHost);

      const filename = `salary-slip-${slipMonth.replace(/\s+/g, "-")}.pdf`;
      const pdfOptions = {
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      if (Capacitor.isNativePlatform()) {
        // .toPdf() must run before .outputPdf() so the jsPDF instance exists
        const blob = await html2pdf()
          .set(pdfOptions)
          .from(exportNode)
          .toPdf()
          .outputPdf("blob");
        await savePdfBlob(blob, filename);
      } else {
        await html2pdf().set(pdfOptions).from(exportNode).save();
      }
    } catch (error) {
      console.error("[handleDownloadPdf] error:", error);
      toast.error("Unable to download salary slip. Please try again.");
    } finally {
      if (exportHost?.parentNode) exportHost.parentNode.removeChild(exportHost);
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="salary-slip-page">
        <div className="salary-slip-card">Loading salary slip...</div>
      </div>
    );
  }

  if (!user || !payroll) {
    return (
      <div className="salary-slip-page">
        <div className="salary-slip-card">
          Unable to load salary slip. Please try again later.
        </div>
      </div>
    );
  }

  const rows = [
    {
      label: "Basic",
      standard: payroll.basic,
      earned: payroll.earnedBasic,
      deductionLabel: "EPF @12%",
      deductionValue: payroll.epf,
    },
    {
      label: "DA",
      standard: payroll.da,
      earned: payroll.earnedDa,
      deductionLabel: "ESIC @0.75%",
      deductionValue: payroll.esic,
    },
    {
      label: "HRA",
      standard: payroll.hra,
      earned: payroll.earnedHra,
      deductionLabel: "LWF",
      deductionValue: payroll.lwfDeduction,
    },
    {
      label: "Other Allowance",
      standard: payroll.otherAllowance,
      earned: payroll.earnedOtherAllowance,
      deductionLabel: "P.tax",
      deductionValue: payroll.professionalTax,
    },
    {
      label: "LWW",
      standard: payroll.lww,
      earned: payroll.earnedLww,
      deductionLabel: "Advance",
      deductionValue: payroll.advance,
    },
    {
      label: "Incentive",
      standard: payroll.incentive,
      earned: payroll.earnedIncentive,
    },
  ];

  return (
    <div className="salary-slip-page">
      <div className="salary-slip-toolbar">
        <div className="salary-slip-filters-row">
          <label>
            Month
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          </label>
          <label>
            Year
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          className="print-btn salary-slip-download-btn"
          onClick={handleDownloadPdf}
          disabled={isDownloading}
        >
          {isDownloading ? "Generating…" : "Download Slip"}
        </button>
      </div>
      <div className="salary-slip-card" ref={slipRef}>
        <div className="salary-slip-form-header">
          <p>FORM XIX</p>
          <p>(Central Rule 78(1) (b))</p>
          <h3>Wage Slip</h3>
        </div>
        <div className="salary-slip-company-top">
          <p className="salary-slip-label">Company</p>
          <h2 className="salary-slip-title">
            {(user.hired?.establishment_id?.name &&
              user.hired.establishment_id.name.toUpperCase()) ||
              "COMPLYMAX MANAGEMENT PRIVATE LIMITED"}
          </h2>
          <p className="salary-slip-company-address">
            {user.hired?.establishment_id?.address ||
              "Plot No-15A Ground Floor, Gali No. 5, Phase-1, Jai Vihar, Baprola, Delhi, 110043"}
          </p>
          <p className="salary-slip-period">{slipMonth}</p>
        </div>

        <div className="employee-info-grid">
          {[
            {
              label: "Employee ID",
              value: user.employeeId ? `CMPL${user.employeeId}` : "N/A",
            },
            { label: "Employee Name", value: user.full_Name },
            { label: "Father Name", value: user.care_of || "N/A" },
            { label: "UAN No", value: user.uan_number || "N/A" },
            { label: "ESIC No", value: user.esic_number || "N/A" },
            { label: "Bank Account No", value: user.account_number || "N/A" },
            { label: "IFSC Code", value: user.account_ifsc || "N/A" },
            {
              label: "Designation",
              value:
                user.hired?.hiring_id?.job_category ||
                user.hired?.designation ||
                "Employee",
            },
            {
              label: "Present Days",
              value: `${presentDays} Days`,
            },
            {
              label: "Pay Month",
              value: slipMonth,
            },
            {
              label: "DOJ",
              value: user.hired?.date_of_joining
                ? moment(user.hired.date_of_joining)
                    .tz("Asia/Kolkata")
                    .format("DD MMM YYYY")
                : user.date_of_joining
                ? moment(user.date_of_joining)
                    .tz("Asia/Kolkata")
                    .format("DD MMM YYYY")
                : "N/A",
            },
            {
              label: "DOB",
              value: user.dob
                ? moment(user.dob).tz("Asia/Kolkata").format("DD MMM YYYY")
                : "N/A",
            },
          ].map((detail) => (
            <div key={detail.label}>
              <p className="detail-label">{detail.label}</p>
              <p className="detail-value">{detail.value}</p>
            </div>
          ))}
        </div>

        <div className="table-scroll">
          <table className="salary-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Standard Wages</th>
                <th>Earned Wages</th>
                <th>Deduction</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>₹{formatCurrency(row.standard)}</td>
                  <td>₹{formatCurrency(row.earned)}</td>
                  <td>{row.deductionLabel || ""}</td>
                  <td>
                    {row.deductionValue
                      ? `₹${formatCurrency(row.deductionValue)}`
                      : ""}
                  </td>
                </tr>
              ))}
              <tr className="summary-row">
                <td>Gross</td>
                <td>₹{formatCurrency(payroll.gross)}</td>
                <td>₹{formatCurrency(payroll.earnedGross)}</td>
                <td>Total Deduction</td>
                <td>₹{formatCurrency(payroll.totalDeduction)}</td>
              </tr>
              <tr className="summary-row">
                <td colSpan="3"></td>
                <td>Net Take Home</td>
                <td>₹{formatCurrency(payroll.netTakeHome)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="salary-notes">
          <p>
            This payslip is system generated and does not require a signature.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SalarySlip;
